import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.94) 0%, rgba(10,13,17,.94) 100%)",
  border: "1px solid rgba(255,255,255,.08)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
  borderSoft: "1px solid rgba(51,65,85,.8)",
};

const fmtUsd = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "$0.00";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const fmtTime = (ts) => {
  try {
    const d =
      ts && typeof ts.toDate === "function"
        ? ts.toDate()
        : typeof ts === "number"
        ? new Date(ts)
        : ts
        ? new Date(ts)
        : null;
    if (!d) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
};

function typeChip(t) {
  const raw = String(t || "").toLowerCase();

  let baseType = raw;
  if (raw.startsWith("deposit")) baseType = "deposit";
  else if (raw.startsWith("withdraw")) baseType = "withdraw";
  else if (raw.startsWith("settle")) baseType = "settle";

  const tone =
    baseType === "deposit"
      ? "rgba(34,197,94,.18)"
      : baseType === "withdraw"
      ? "rgba(249,115,115,.18)"
      : baseType === "settle"
      ? "rgba(56,189,248,.18)"
      : "rgba(148,163,184,.14)";

  return (
    <Chip
      label={(baseType || "entry").toUpperCase()}
      size="small"
      sx={{
        height: 22,
        borderRadius: 999,
        fontWeight: 900,
        fontSize: 11,
        bgcolor: tone,
        border: GLASS.borderSoft,
        color: GLASS.text,
      }}
    />
  );
}

function TableContent({ data }) {
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: GLASS.subtext, fontWeight: 900, borderColor: "rgba(255,255,255,.08)" }}>
              Time
            </TableCell>
            <TableCell sx={{ color: GLASS.subtext, fontWeight: 900, borderColor: "rgba(255,255,255,.08)" }}>
              Type
            </TableCell>
            <TableCell sx={{ color: GLASS.subtext, fontWeight: 900, borderColor: "rgba(255,255,255,.08)" }}>
              Market
            </TableCell>
            <TableCell sx={{ color: GLASS.subtext, fontWeight: 900, borderColor: "rgba(255,255,255,.08)" }}>
              Side
            </TableCell>
            <TableCell sx={{ color: GLASS.subtext, fontWeight: 900, borderColor: "rgba(255,255,255,.08)" }}>
              Amount
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {(!data || data.length === 0) ? (
            <TableRow>
              <TableCell colSpan={5} sx={{ color: GLASS.subtext, borderColor: "rgba(255,255,255,.08)" }}>
                No ledger entries yet.
              </TableCell>
            </TableRow>
          ) : (
            data.map((e) => (
              <TableRow
                key={e.id || e.txHash || `${e.type || "entry"}_${e.timestamp || e.ts || e.createdAt || ""}`}
              >
                <TableCell sx={{ color: GLASS.text, borderColor: "rgba(255,255,255,.08)" }}>
                  {fmtTime(e.timestamp || e.ts || e.createdAt)}
                </TableCell>

                <TableCell sx={{ borderColor: "rgba(255,255,255,.08)" }}>
                  {typeChip(e.type)}
                </TableCell>

                <TableCell sx={{ color: GLASS.text, borderColor: "rgba(255,255,255,.08)" }}>
                  {e.marketTitle || e.marketId || "—"}
                </TableCell>

                <TableCell sx={{ color: GLASS.text, borderColor: "rgba(255,255,255,.08)" }}>
                  {e.side ? String(e.side).toUpperCase() : "—"}
                </TableCell>

                <TableCell sx={{ color: "#e5e7eb", borderColor: "rgba(255,255,255,.08)", fontWeight: 900 }}>
                  {fmtUsd(e.amount)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function LedgerTable({ rows, ledger, embedded = false }) {
  const data = rows ?? ledger ?? [];

  if (embedded) return <TableContent data={data} />;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        background: GLASS.bg,
        border: GLASS.border,
        boxShadow: "0 18px 40px rgba(15,23,42,.9)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2.1 }}>
        <Typography sx={{ fontWeight: 950, fontSize: 14, color: GLASS.text, mb: 1 }}>
          Ledger
        </Typography>
        <TableContent data={data} />
      </CardContent>
    </Card>
  );
}
